import React, { useState } from 'react';
import { ReactComponent as ArrowDown } from '../../assets/icons/arrow_down.svg';
import { ReactComponent as ArrowUp } from '../../assets/icons/arrow_up.svg';
import { Link } from 'react-router-dom';
import ContactModal from '../contactModal/ContactModal';
import WatchCardModal from '../watchCardModal/WatchCardModal';
import { useLanguage, Language } from '../../context/LanguageContext';
import { useEditMode } from '../../context/EditModeContext';
import { useRegion, SUPPORTED_REGIONS, Region } from '../../context/RegionContext';
import { useLocation, useNavigate } from 'react-router-dom';
import './Footer.css';
import { isMobile } from '../../utils/appUtils';
import LoginModal from '../auth/LoginModal';

interface FooterTranslations {
  customerCare: string;
  contactUs: string;
  downloadWatchCard: string;
  ourCompany: string;
  findStore: string;
  legalArea: string;
  imprint: string;
  patents: string;
  regionSettings: string;
  selectRegion: string;
  forDealers: string;
  dealerPortal: string;
}

export type FooterTranslationKey = keyof FooterTranslations;

type FooterLink = {
  id: FooterTranslationKey;
  path: string;
  isModal?: boolean;
  onClick?: () => void;
};

type FooterSection = {
  title: FooterTranslationKey;
  items: FooterLink[];
};

const footerTranslations: Partial<Record<Language, FooterTranslations>> = {
  en: {
    customerCare: "Customer Care",
    contactUs: "Contact Us",
    downloadWatchCard: "Download Watch Card",
    ourCompany: "Our Company",
    findStore: "Find a Store",
    legalArea: "Legal Area",
    imprint: "Imprint",
    patents: "Patents",
    regionSettings: "Region & Currency",
    selectRegion: "Select Region",
    forDealers: "For Dealers",
    dealerPortal: "Dealer Portal"
  },
  de: {
    customerCare: "Kundenservice",
    contactUs: "Kontakt",
    downloadWatchCard: "Uhrenzertifikat Herunterladen",
    ourCompany: "Unser Unternehmen",
    findStore: "Händlersuche",
    legalArea: "Rechtliches",
    imprint: "Impressum",
    patents: "Patente",
    regionSettings: "Region & Währung",
    selectRegion: "Region Wählen",
    forDealers: "Für Händler",
    dealerPortal: "Händlerbereich"
  },
  zh: {
    customerCare: "客戶服務",
    contactUs: "聯絡我們",
    downloadWatchCard: "下载手表证书",
    ourCompany: "我們的公司",
    findStore: "尋找商店",
    legalArea: "法律領域",
    imprint: "版本說明",
    patents: "專利",
    regionSettings: "地區與貨幣",
    selectRegion: "選擇地區",
    forDealers: "经销商专区",
    dealerPortal: "经销商登录"
  },
  fr: {
    customerCare: "Service Client",
    contactUs: "Nous Contacter",
    downloadWatchCard: "Télécharger la Carte de Montre",
    ourCompany: "Notre Maison",
    findStore: "Trouver une Boutique",
    legalArea: "Mentions Légales",
    imprint: "Empreinte",
    patents: "Brevets",
    regionSettings: "Région et Devise",
    selectRegion: "Sélectionner la Région",
    forDealers: "Pour les Revendeurs",
    dealerPortal: "Espace Revendeur"
  }
};

function getFooterTranslations(language: Language): FooterTranslations {
  return footerTranslations[language] || footerTranslations.en!;
}

const Footer: React.FC = () => {
  const { language } = useLanguage();
  const { region, setRegion, regionInfo } = useRegion();
  const { isEditMode, toggleEditMode, setEditModeEnabled } = useEditMode();
  const location = useLocation();
  const navigate = useNavigate();
  const [expandedSections, setExpandedSections] = useState<string[]>([]);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isWatchCardModalOpen, setIsWatchCardModalOpen] = useState(false);
  const [isRegionDropdownOpen, setIsRegionDropdownOpen] = useState(false);
  const [isDealerLoginOpen, setIsDealerLoginOpen] = useState(false);
  const t = getFooterTranslations(language);

  const footerSections: FooterSection[] = [
    {
      title: 'customerCare',
      items: [
        {
          id: 'contactUs',
          path: '#',
          isModal: true,
          onClick: () => setIsContactModalOpen(true)
        },
        {
          id: 'downloadWatchCard',
          path: '#',
          isModal: true,
          onClick: () => setIsWatchCardModalOpen(true)
        }
      ],
    },
    {
      title: 'ourCompany',
      items: [
        { id: 'findStore', path: '/stores/stores' }
      ],
    },
    {
      title: 'legalArea',
      items: [
        { id: 'imprint', path: '/imprint' },
        { id: 'patents', path: '/maison/patents' }
      ],
    },
    {
      title: 'forDealers',
      items: [
        {
          id: 'dealerPortal',
          path: '#',
          isModal: true,
          onClick: () => setIsDealerLoginOpen(true)
        }
      ],
    },
    {
      title: 'regionSettings',
      items: [
        {
          id: 'selectRegion',
          path: '#',
          isModal: true,
          onClick: () => setIsRegionDropdownOpen(!isRegionDropdownOpen)
        }
      ],
    },
  ];

  const toggleSection = (title: FooterTranslationKey) => {
    setExpandedSections((prev) =>
      prev.includes(title)
        ? prev.filter((section) => section !== title)
        : [...prev, title]
    );
  };

  const isSectionExpanded = (title: string) => expandedSections.includes(title);

  const handleEditClick = () => {
    if (!isEditMode) {
      // Preserve current URL path and add edit=true parameter
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('edit', 'true');

      // Update URL and explicitly set edit mode to true
      navigate(`${location.pathname}?${searchParams.toString()}`);
      setEditModeEnabled(true);
    } else {
      toggleEditMode();
    }
  };

  return (
    <>
      <footer className="footer mt-2 md:mt-12 mb-6 md:mb-0">
        {footerSections.map((section) => (
          <div key={section.title} className="footer-section">
            <h4
              className={`footer-sub-title ${isSectionExpanded(section.title) ? 'active' : ''}`}
              onClick={() => toggleSection(section.title)}
            >
              {t[section.title]}
              <span className="footer-arrow">
                {isSectionExpanded(section.title) ? <ArrowUp className='footer-arrow-up' /> : <ArrowDown />}
              </span>
            </h4>
            <div className={`footer-content ${isSectionExpanded(section.title) ? 'expanded' : ''}`}>
              {section.items.map((item) => (
                item.isModal ? (
                  section.title === 'regionSettings' ? (
                    // On mobile, show regions inline instead of nested dropdown
                    isMobile() ? (
                      <div key={item.id} className="block">
                        <p className="flex items-center justify-between">
                          {t.selectRegion}
                        </p>
                        <div className="mt-2 border border-gray-200 rounded-md overflow-hidden">
                          {Object.values(SUPPORTED_REGIONS).map((regionOption) => (
                            <div
                              key={regionOption.code}
                              onClick={() => {
                                setRegion(regionOption.code);
                              }}
                              className={`p-3 cursor-pointer hover:bg-gray-50 ${
                                region === regionOption.code ? 'bg-gray-100 font-medium' : ''
                              }`}
                            >
                              <p className="text-sm">
                                {regionOption.name} ({regionOption.currency})
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      // Desktop: keep existing dropdown behavior
                      <div key={item.id} className="relative">
                        <div
                          onClick={item.onClick}
                          className="block cursor-pointer"
                        >
                          <p className="flex items-center justify-between">
                            {regionInfo.name} ({regionInfo.currency})
                            <span className="ml-2">
                              {isRegionDropdownOpen ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                            </span>
                          </p>
                        </div>
                        {isRegionDropdownOpen && (
                          <div className="absolute bottom-full left-0 right-0 bg-white border border-gray-200 rounded-md shadow-lg z-10 mb-2">
                            {Object.values(SUPPORTED_REGIONS).map((regionOption) => (
                              <div
                                key={regionOption.code}
                                onClick={() => {
                                  setRegion(regionOption.code);
                                  setIsRegionDropdownOpen(false);
                                }}
                                className={`p-3 cursor-pointer hover:bg-gray-50 ${
                                  region === regionOption.code ? 'bg-gray-100 font-medium' : ''
                                }`}
                              >
                                <p className="text-sm">
                                  {regionOption.name} ({regionOption.currency})
                                </p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )
                  ) : (
                    <div
                      key={item.id}
                      onClick={item.onClick}
                      className="block cursor-pointer"
                    >
                      <p>{t[item.id]}</p>
                    </div>
                  )
                ) : (
                  <Link key={item.id} to={item.path} className="block">
                    <p>{t[item.id]}</p>
                  </Link>
                )
              ))}
            </div>
          </div>
        ))}

        {process.env.REACT_APP_ENABLE_API === 'true' && (
          <div className='mt-6 flex justify-between items-center'>
            <button
              onClick={handleEditClick}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200 flex items-center"
              title="Edit content"
              aria-label="Edit page content"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
              <span className="text-xs">Edit</span>
            </button>
          </div>
        )}
      </footer>
      <ContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
      <WatchCardModal
        isOpen={isWatchCardModalOpen}
        onClose={() => setIsWatchCardModalOpen(false)}
      />
      {isDealerLoginOpen && (
        <LoginModal
          onClose={() => setIsDealerLoginOpen(false)}
          onSuccess={() => {
            setIsDealerLoginOpen(false);
            navigate('/create-certificate');
          }}
          title="Dealer Access"
          description="Sign in to create watch certificates."
          submitLabel="Continue"
        />
      )}
    </>
  );
};

export default Footer;
